import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Skeleton } from 'antd';

import { IProducts } from '../service/products.interface';
import {
    useReadProductByIdQuery,
    useUpdateProductMutation,
} from '../service/products';
import { createFormData } from '../../../../../utils/common/createFormData';
import { ProductForm } from '../components/ProductForm';
import { useAppSelector } from '../../../../../store';
import { selectShopCurrent } from '../../shops/store/shopSlice';

export const ProductEditContainer = () => {
    const [initial, setInitial] = useState<IProducts>();

    const { notification } = App.useApp();
    const { productId, type } = useParams();
    const navigate = useNavigate();
    const isLinkPage = type === 'links';

    const current = useAppSelector(selectShopCurrent);

    const [
        updateProduct,
        { isSuccess: isSuccessUpdate, isError: isErrorUpdate },
    ] = useUpdateProductMutation();

    const {
        data: product,
        isSuccess,
        isLoading,
    } = useReadProductByIdQuery(productId);

    useEffect(() => {
        if (isSuccessUpdate) {
            notification.success({
                message: isLinkPage
                    ? 'Витрина успешно изменена'
                    : 'Товар успешно изменен',
            });
            navigate(-1);
        }
    }, [isSuccessUpdate]);

    useEffect(() => {
        if (isErrorUpdate) {
            notification.error({
                message: 'Произошла ошибка',
            });
        }
    }, [isErrorUpdate]);

    useEffect(() => {
        if (isSuccess) {
            const categoriesIds = product.categories.map(
                category => category.id
            );
            setInitial({
                shop_id: current.id,
                description: product.description,
                price: product.price,
                old_price: product.old_price,
                title: product.title,
                images: product.images,
                image_urls: product.image_urls,
                buyable: product.buyable,
                category_ids: categoriesIds,
                status: product?.status,
                link: product?.link,
                type: product?.type,
            });
        }
    }, [isSuccess]);

    const onSave = async (values: IProducts) => {
        if (isLinkPage && !values.link) {
            return notification.error({
                message: 'Нужно указать ссылку на товар',
            });
        }

        if (values.old_price && values.price > Number(values.old_price)) {
            return notification.error({
                message: `Цена без скидки не может быть меньше ${values.price}`,
            });
        }

        const data = {
            shop_id: current.id,
            title: values.title,
            description: values.description,
            price: values.price,
            old_price: values.old_price,
            images: values.images,
            buyable: values.buyable,
            category_ids: values.category_ids,
            status: values.status,
            link: values.link,
            type: values.type,
        };

        if (!values.description) {
            delete data.description;
        }

        if (!values.old_price) {
            delete data.old_price;
        }

        if (values.images) {
            delete data.images;
        }

        if (!isLinkPage) {
            delete data.link;
        }

        if (isLinkPage && !values.price) {
            data.price = 0;
        }

        await updateProduct({ id: current.id, body: createFormData(data) });
    };

    const isDisableEditProduct = useMemo(() => {
        return product?.external_code !== null && !isLinkPage;
    }, [isLinkPage, product]);

    return isLoading ? (
        <Skeleton />
    ) : (
        initial && (
            <ProductForm
                initialValues={initial}
                onSave={onSave}
                isDisableEditProduct={isDisableEditProduct}
            />
        )
    );
};
